/**
 * The Results tab's edit for one winner (ISSUE-1293), after v1's
 * single_winner_strip_selection.dart: its date and its note, saved with
 * `/strips/winner-update`. Closing with unsaved edits asks first.
 */
import { useStripsWinnerUpdate } from "@sportsmet/contest-sdk/private";
import { Button, confirmDestructive, Dialog } from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import { resolveViewerTimeZone } from "../scores/viewerTimeZone";
import {
  STRIP_CANCEL_LABEL,
  STRIP_DISCARD_BODY,
  STRIP_DISCARD_LABEL,
  STRIP_DISCARD_TITLE,
  STRIP_WINNER_EDIT_SAVE_LABEL,
  STRIP_WINNER_EDIT_TITLE,
} from "./stripCardCopy";
import { StripWinnerFields } from "./StripWinnerFields";
import {
  buildWinnerDrafts,
  isWinnerDraftDirty,
  type typeStripWinnerDraft,
  winnerDraftErrors,
  winnerDraftsBody,
} from "./stripWinnerDraft";

export type typeStripResultsWinner = {
  stripWinnerId: string;
  wonAt: string;
  note: string;
  displayName: string;
  stripLabel: string;
  rowLabel: string;
  digit: number;
};

export const StripWinnerEditSheet = (props: {
  poolId: string;
  winner: typeStripResultsWinner | null;
  onClose: () => void;
}): ReactNode => {
  const timeZone = resolveViewerTimeZone();
  const update = useStripsWinnerUpdate();
  const { winner } = props;
  const [initial, setInitial] = useState<typeStripWinnerDraft[]>([]);
  const [drafts, setDrafts] = useState<typeStripWinnerDraft[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const winnerId = winner?.stripWinnerId ?? "";

  useEffect(() => {
    if (winner === null) return;
    const seeded = buildWinnerDrafts({ winners: [winner], timeZone });
    setInitial(seeded);
    setDrafts(seeded);
    setIsSubmitted(false);
    // Seed once per winner opened, never on a background refetch.
  }, [winnerId]);

  if (winner === null) return null;
  const draft = drafts[0];
  const isDirty = isWinnerDraftDirty({ initial, drafts });
  const errors = isSubmitted ? winnerDraftErrors(drafts) : {};

  async function requestClose(): Promise<void> {
    if (!isDirty) {
      props.onClose();
      return;
    }
    const isConfirmed = await confirmDestructive({
      title: STRIP_DISCARD_TITLE,
      body: STRIP_DISCARD_BODY,
      confirmLabel: STRIP_DISCARD_LABEL,
    });
    if (isConfirmed) props.onClose();
  }

  async function save(): Promise<void> {
    if (winner === null) return;
    setIsSubmitted(true);
    const body = winnerDraftsBody(drafts)?.[0];
    if (body === undefined) return;
    let result: Awaited<ReturnType<typeof update.mutateAsync>>;
    try {
      result = await update.mutateAsync({
        poolId: props.poolId,
        stripWinnerId: winner.stripWinnerId,
        wonAt: body.wonAt,
        note: body.note,
      });
    } catch {
      // The app's MutationCache already raised the offline modal.
      return;
    }
    if (showApiError(result)) return;
    props.onClose();
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
      title={STRIP_WINNER_EDIT_TITLE}
      description={`${winner.displayName}, ${winner.stripLabel} row ${winner.rowLabel}, digit ${String(winner.digit)}`}
      isDirty={isDirty}
      onRequestClose={() => {
        void requestClose();
      }}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              void requestClose();
            }}
          >
            {STRIP_CANCEL_LABEL}
          </Button>
          <Button
            variant="primary"
            data-testid="strip-winner-edit-save"
            disabled={!isDirty}
            loading={update.isPending}
            onClick={() => {
              void save();
            }}
          >
            {STRIP_WINNER_EDIT_SAVE_LABEL}
          </Button>
        </>
      }
    >
      <div data-testid="strip-winner-edit-sheet">
        {draft !== undefined ? (
          <StripWinnerFields
            index={0}
            heading={winner.displayName}
            draft={draft}
            error={errors[draft.key]}
            onChange={(change) => {
              setDrafts((current) =>
                current.map((candidate) => ({ ...candidate, ...change })),
              );
            }}
          />
        ) : null}
      </div>
    </Dialog>
  );
};
